import { parseConfig, getLazyLoadComponentObserver } from '@utilities/theme';
import { EVENTS, publish, subscribe } from '@utilities/events';

/**
 * @fileoverview Custom element that handles product promotions in a Shopify theme.
 *
 * This component fetches and displays promotional messages based on product information.
 * It supports lazy loading, configurable templates, and fallback fetch strategies.
 *
 * @example
 * <!-- Basic usage in Liquid template -->
 * <product-promotion class="product__promotion lazy-load">
 *   <script type="application/json" component-config>
 *     {
 *       "template": "card",
 *       "fetch": {
 *         "url": "{{ selected_variant.url }}"
 *       }
 *     }
 *   </script>
 *   <div class="product__promotion__inner"></div>
 * </product-promotion>
 */
/**
 * Register the custom element if it hasn't been defined yet
 */
if(!customElements.get('product-promotion')) {
  /**
   * @class ProductPromotion
   * @extends {HTMLElement}
   * @description Custom element for fetching and displaying product promotions.
   * The component can render different promotional messages based on the context
   * (product detail page or collection card).
   */
  class ProductPromotion extends HTMLElement {
    /**
     * @constructor
     * @description Initializes the ProductPromotion component
     */
    constructor() {
      super();

      /**
       * @property {boolean} lazyLoad - Whether the component should use lazy loading
       * @private
       */
      this.lazyLoad = this.classList.contains("lazy-load");

      /**
       * @property {Object} config - Configuration settings parsed from embedded JSON
       * @private
       */
      this.config = {};

      /**
       * @property {Object} promotion - The promotion data fetched from the server
       * @private
       */
      this.promotion = {};
    }

    /**
     * @method allowDebug
     * @description Checks if the component is in debug mode.
     * @returns {boolean} True if the component is in debug mode, false otherwise.
     * @private
     */
    allowDebug() {
      return this.classList.contains('debug');
    }

    /**
     * @method connectedCallback
     * @description Lifecycle callback when element is added to the DOM.
     * Parses the configuration and initializes either immediately or via lazy loading.
     * @async
     */
    async connectedCallback() {
      this.config = parseConfig(this);
      this.productOverview = this.closest('product-overview');
      this.subscribeToEvents();

      if (this.lazyLoad) {
        if(this.allowDebug()) {
          console.log(`ProductPromotion is lazy-loading`);
        }
        getLazyLoadComponentObserver().observe(this);
      } else {
        if(this.allowDebug()) {
          console.log(`ProductPromotion is ready to initialize`);
        }
        await this.init();
      }
    }

    /**
     * @method subscribeToEvents
     * @description Sets up pub-sub subscriptions for variant changes.
     * Only subscribes when inside a product-overview (PDP context).
     * @private
     */
    subscribeToEvents() {
      this.unsubscribers = [];

      if (this.productOverview) {
        this.unsubscribers.push(
          subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, (data) => {
            if (data.productOverview !== this.productOverview) return;
            if (!data.variant?.url) return;
            this.updateFetchData({ url: data.variant.url });
          })
        );
      }
    }

    /**
     * @method disconnectedCallback
     * @description Lifecycle callback when element is removed from the DOM.
     * Cleans up pub-sub subscriptions to prevent memory leaks.
     */
    disconnectedCallback() {
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
    }

    /**
     * @method init
     * @description Initializes the component, fetching data and setting up the component.
     * This is called immediately or by the lazy load observer when the element enters viewport.
     * @async
     * @private
     */
    async init() {
      if(this.allowDebug()) {
        console.log(`ProductPromotion is initializing`);
      }
      this.observer = null;
      await this.initiateDataFetch();
      this.classList.add('product-promotion--initialized');
      this.lazyLoad = false;
    }

    /**
     * @method initiateDataFetch
     * @description Sets up the promotion container and initiates the data fetch process.
     * @async
     * @private
     */
    async initiateDataFetch() {
      if(this.allowDebug()) {
        console.log(`ProductPromotion is initiating data fetch`);
      }
      this.promotionContainer = this.querySelector('.product__promotion__inner');
      this.renderingOn = this.config.template || this.dataset.template;

      if (this.config.fetch) {
        await this.fetchPromotionWithData(this.config.fetch);
      }
    }

    /**
     * @method fetchPromotionWithData
     * @description Fetches promotion data using the provided fetch configuration.
     * If the main fetch fails or returns empty, uses backup URL if provided.
     * @param {Object} fetchData - The fetch configuration object
     * @param {string} fetchData.url - The primary URL to fetch promotion data from
     * @param {Object} [fetchData.backup] - Optional backup fetch configuration
     * @param {string} fetchData.backup.url - The backup URL to fetch from if primary fails
     * @async
     * @private
     */
    async fetchPromotionWithData(fetchData) {
      if (!fetchData.url) return;
      if(this.allowDebug()) {
        console.log(`ProductPromotion is fetching promotion with data from ${fetchData.url}`);
      }
      const response = await this.fetchPromotion(fetchData.url);

      if (Object.keys(response).length === 0 && fetchData.backup?.url) {
        if(this.allowDebug()) {
          console.log(`ProductPromotion is fetching backup promotion with data from ${fetchData.backup.url}`);
        }
        // Try backup data if main fetch returns empty
        const backupResponse = await this.fetchPromotion(
          fetchData.backup.url
        );
        this.updatePromotionData(backupResponse);
      } else {
        if(this.allowDebug()) {
          console.log(`ProductPromotion is updating promotion with data from ${fetchData.url}`);
        }
        this.updatePromotionData(response);
      }
    }

    /**
     * @method fetchPromotion
     * @description Fetches promotion data from the specified URL.
     * @param {string} url - The URL to fetch promotion data from
     * @returns {Promise<Object>} Promise resolving to the promotion data
     * @async
     * @private
     */
    async fetchPromotion(url = '') {
      if(!String(url).length > 0) return {};
      if (this.allowDebug()) {
        void this.fetchPromotionDebug(url);
      }

      const fetchUrl = `${url}&view=promotion&timestamp=${Math.floor(Date.now() / 1000)}`;
      try {
        const response = await fetch(`${fetchUrl}`);
        return await response.json();
      } catch (error) {
        console.error('Error fetching promotion:', error);
        return {};
      }
    }

    /**
     * @method fetchPromotionDebug
     * @description Sends a fire-and-forget debug request when debug mode is enabled.
     * @param {string} url - The base product URL for debug fetch
     * @returns {Promise<void>}
     * @async
     * @private
     */
    async fetchPromotionDebug(url = '') {
      if(!String(url).length > 0) return;

      const fetchUrl = `${url}&view=promotion-debug&timestamp=${Math.floor(Date.now() / 1000)}`;
      try {
        await fetch(`${fetchUrl}`);
      } catch (error) {
        console.error('Error fetching promotion debug:', error);
      }
    }

    /**
     * @method updatePromotionData
     * @description Updates the component with new promotion data and triggers rendering.
     * @param {Object} data - The promotion data
     * @fires ProductPromotion#product-promotion:change
     * @private
     */
    updatePromotionData(data) {
      try {
        // Check if the promotion data has actually changed
        const dataString = JSON.stringify(data);
        const currentDataString = JSON.stringify(this.promotion);
        const isUnchanged = dataString === currentDataString;

        this.promotion = data;

        // Publish even when the payload is unchanged. The payload is identical, but the
        // variant it was resolved for may not be: a variant change refetches this
        // promotion, and subscribers such as <product-price> need that confirmation to
        // know the promotion covers the variant now on screen. A subscriber cannot tell
        // "unchanged" from "never arrived", so withholding the event strands it.
        publish(EVENTS.PRODUCT_PROMOTION_CHANGE, {
          triggeringElement: this,
          promotion: data,
        });

        // The message markup only needs redrawing when the payload actually changed.
        if (isUnchanged) {
          if(this.allowDebug()) {
            console.log(`ProductPromotion updatePromotionData: Data unchanged, skipping render`);
          }
          return;
        }

        this.renderPromotion();
      } catch (error) {
        console.error('Error updating promotion data:', error);
      }
    }

    /**
     * @method renderPromotion
     * @description Renders the promotion message in the container with appropriate styling.
     * @private
     */
    renderPromotion() {
      if(this.allowDebug()) {
        console.log(`ProductPromotion is rendering promotion`, this.promotionContainer, this.promotion);
      }
      if (!this.promotionContainer) return;

      // Clear any existing timeout to prevent multiple renders
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }

      // The fade-out only has something to hide when content is already on screen.
      // On the first render the container is empty, so waiting for the transition adds
      // a delay the shopper reads as the promotion being slow to appear.
      const hasExistingContent = this.promotionContainer.childElementCount > 0;
      const fadeDuration = hasExistingContent ? 300 : 0;

      if (hasExistingContent) {
        // Add fade-out class to trigger transition
        this.promotionContainer.classList.add("fade-out");
      }

      // Wait for fade out transition to complete before updating content
      this.renderTimeout = setTimeout(() => {
        if(this.allowDebug()) {
          console.log(`ProductPromotion setTimeout executing for promotion update`);
        }

        // Clear existing styles and content
        this.promotionContainer.classList.remove("has-bg");
        this.promotionContainer.style.color = "";
        this.promotionContainer.style.backgroundColor = "";
        this.promotionContainer.replaceChildren();

        if(this.promotion && Object.keys(this.promotion).length > 0) {
          // grab the promotional message
          const promotionMessage = this.determinePromotionMessage();
          if(this.allowDebug()) {
            console.log(`ProductPromotion is determining promotion message`, promotionMessage);
          }

          if(promotionMessage.length > 0) {
            if(this.allowDebug()) {
              console.log(`ProductPromotion has valid message, rendering content`);
            }

            const fragment = document.createDocumentFragment();
            // Create a new paragraph tag for content
            const contentDiv = document.createElement('p');
            contentDiv.textContent = promotionMessage;
            fragment.appendChild(contentDiv);

            this.promotionContainer.style.color = this.promotion?.fields?.text_color || "";
            this.promotionContainer.style.backgroundColor = this.promotion?.fields?.background_color || "";

            if(this.promotion.fields?.background_color) {
              this.promotionContainer.classList.add("has-bg");
            }

            // Add new content
            this.promotionContainer.appendChild(fragment);

            // Force a reflow to ensure the transition happens
            this.promotionContainer.offsetHeight;

            // Remove fade-out to trigger fade in
            this.promotionContainer.classList.remove("fade-out");
          } else {
            if(this.allowDebug()) {
              console.log(`ProductPromotion has empty message, removing fade-out class`);
            }
            // Remove fade-out even if no message to avoid leaving element in fade-out state
            this.promotionContainer.classList.remove("fade-out");
          }
        } else {
          if(this.allowDebug()) {
            console.log(`ProductPromotion has no promotion data, removing fade-out class`);
          }
          // Remove fade-out even if no promotion data
          this.promotionContainer.classList.remove("fade-out");
        }

        this.renderTimeout = null;
      }, fadeDuration); // Match this to the CSS transition duration
    }

    /**
     * @method determinePromotionMessage
     * @description Determines which promotion message to display based on rendering context.
     * @returns {string} The appropriate promotion message text
     * @private
     */
    determinePromotionMessage() {
      if(this.allowDebug()) {
        console.log(`ProductPromotion determinePromotionMessage debug:`, {
          renderingOn: this.renderingOn,
          promotionFields: this.promotion.fields,
          cardText: this.promotion.fields?.collection_text,
          pdpText: this.promotion.fields?.product_text,
          cartText: this.promotion.fields?.cart_text,
          title: this.promotion.fields?.title
        });
      }

      if(this.renderingOn == "card") {
        return this.promotion.fields?.collection_text || "";
      }
      if(this.renderingOn == "pdp") {
        return this.promotion.fields?.product_text || "";
      }
      if(this.renderingOn == "cart") {
        return this.promotion.fields?.cart_text || "";
      }
      return this.promotion.fields?.title || "";
    }

    /**
     * @method getPromotion
     * @description Returns the current promotion data.
     * @returns {Object} The current promotion data
     * @public
     */
    getPromotion() {
      return this.promotion;
    }

    /**
     * @method updateFetchData
     * @description Updates the fetch configuration and triggers a re-fetch.
     * @param {Object} newData - New fetch configuration data to merge
     * @public
     */
    updateFetchData(newData) {
      if (newData && typeof newData === 'object') {
        // Check if the URL is actually changing to avoid unnecessary fetches
        const currentUrl = this.config.fetch?.url;
        const newUrl = newData.url;

        if (currentUrl === newUrl) {
          if(this.allowDebug()) {
            console.log(`ProductPromotion updateFetchData: URL unchanged, skipping fetch`, newUrl);
          }
          return;
        }

        if(this.allowDebug()) {
          console.log(`ProductPromotion updateFetchData: URL changed from ${currentUrl} to ${newUrl}`);
        }

        this.config.fetch = {...this.config.fetch, ...newData};
        this.fetchPromotionWithData(this.config.fetch);
      }
    }
  }

  customElements.define('product-promotion', ProductPromotion);
}
